<template>
    <div>
        <b-alert :show="!!errorHandlingMessage" variant="danger" dismissible>{{ errorHandlingMessage }}</b-alert>
        <h3>Workflow History</h3>
        <p class="text-muted">View the full history of all key events on this workflow.</p>

        <div v-if="loading" key="loadingRecords" class="min-height-300 d-flex align-items-center justify-content-center">
            <busy-spinner text="Loading..."></busy-spinner>
        </div>

        <div 
            v-else-if="!loading && !visibleRecords.length" 
            key="noRecords" 
            class="d-flex align-items-center justify-content-center min-height-300"
        >
            <div class="text-muted font-weight-bold">
                No History To Show
            </div>
        </div>

        <Timeline 
            v-else-if="!loading && visibleRecords.length"
            key="hasRecords"
            :value="visibleRecords" 
            dataKey="id" 
            class="workflow-run-history-timeline my-5"
        >
            
            <template #opposite="slotProps">  <!-- use anything in here from slotProps.items -->
                <div class="timeline-opposite-wrapper pb-4">
                    <div class="font-size-90 text-muted">{{slotProps.item.date_formatted}}</div>
                    <div class="font-size-80 text-muted">{{slotProps.item.time_formatted}}</div>
                    <div class="font-size-70 text-muted">{{slotProps.item.zone_formatted}}</div>
                </div>
            </template>

            <template #marker="slotProps">
                <div class="p-timeline-event-marker" :class="slotProps.item.markerClasses"></div>
                <!-- <div v-if="slotProps.index !== (visibleRecords.length - 1)" class="p-timeline-event-connector"></div> -->
            </template>

            <template #content="slotProps">  <!-- use anything in here from slotProps.items -->
                <div class="timeline-content-wrapper pb-4">
                    <div class="font-weight-400">
                        <span v-if="slotProps.item.showActor" class="font-weight-600">{{ slotProps.item.actor.full_name }}&nbsp;</span>
                        <span v-if="slotProps.item.isWorkflowReset">
                            <span class="font-italic">reset the workflow</span>
                            back to task
                        </span>
                        <span v-else-if="slotProps.item.isApprovalEvent">
                            <span class="font-weight-600 font-italic" :class="slotProps.item.wasApproved ? 'text-success-darker' : 'text-danger-darker'">{{ slotProps.item.verb }}</span>
                            the workflow in
                        </span>
                        <span v-else-if="slotProps.item.isTaskEvent">
                            <span class="font-italic">{{ slotProps.item.verb }}</span>
                            the task
                        </span>
                        <span v-if="slotProps.item.taskTitle" class="font-weight-600">
                            <!-- we might have task title, but task was deleted... so only show URL if we still have the task -->
                            <a v-if="slotProps.item.taskUrl" key="hasTaskUrl" :href="slotProps.item.taskUrl" class="text-reset">{{ slotProps.item.taskTitle }}</a>
                            <span v-else key="noTaskUrl">{{ slotProps.item.taskTitle }}</span>
                        </span>
                        <span v-if="slotProps.item.isWorkflowEvent">{{ slotProps.item.workflowEventText }}</span>  <!-- if not hasTask, we'll have this -->
                    </div>
                    <div 
                        v-if="slotProps.item.approvalComment"
                        class="border rounded p-2 m-2 font-size-80 text-show-breaks"
                        :class="slotProps.item.wasApproved ? 'bg-success-50 border-success text-success-darker' : 'bg-danger-50 border-danger text-danger-darker'"
                    >  <!-- Approval Comment -->
                        {{ slotProps.item.approvalComment }}
                    </div>
                    <div 
                        v-if="slotProps.item.message"
                        class="bg-gray-100 border rounded p-2 m-2 font-size-80 text-show-breaks"
                    >  <!-- MESSAGE -->
                        {{ slotProps.item.message }}
                    </div>
                    <div><!-- Put other data stuff here  --></div>
                </div>
            </template>
        </Timeline>
        
        <!-- <pre>{{ visibleRecords }}</pre> -->
    
    </div>
</template>

<script>
import { errorHandling, currentUserInfoMixin } from '../../js/custom-vue-mixins';
import { suppliosApi } from '../../js/api-service';
import Timeline from 'primevue/timeline';
import { toLuxonDateTime, FormatDateTimeHelper } from '../../js/filters-helpers';
import { DateTime } from 'luxon';

//Vue.component(Timeline, 'Timeline');

class TimelineRecord {  // put things in right format for Timeline component
    constructor({  // you can set defaults for constructor params here, or just let them be undefined and set in constructor method
        id,
        created,
        modified,
        verb,  // string
        data,  // nested object
        actor,  // will be the TU object
        task,   // will be task object, but maybe with only a few things
        is_task_event,  // boolean, its how we tell if this was a task-related thing or not, must use this if we were allowing deleting of tasks (like for rollbacks of template tasks)
        workflow_id,  // will be the ID
        isTuInternal,  // added this manually in component when instantiating
    }) {
        // THINGS DIRECTLY FROM SAVED BACKEND DATA
        this.isVisible = true;  // DEFAULT -- IF YOU FIND SOMETHING ALONG THE WAY WHERE WE DON'T WANT TO SHOW ENTIRE RECORD ON FRONT END, SET THIS TO false
        this.isTuInternal = !!isTuInternal;  // need this to know what URLs to show
        this.id = id;
        this.pk = id;
        this.created = toLuxonDateTime(created);
        this.created_formatted = FormatDateTimeHelper(created);
        this.date_formatted = this.created.toLocaleString(DateTime.DATE_HUGE)
        this.time_formatted = this.created.toLocaleString(DateTime.TIME_SIMPLE)
        this.zone_formatted = this.created.toFormat('ZZZZ')  // or 'ZZZZ' or 'ZZZZZ'
        this.modified = toLuxonDateTime(modified);
        this.modified_formatted = FormatDateTimeHelper(modified);
        this.verb = verb ?? '';
        this.data = data ?? {};
        this.actor = actor ?? {};
        this.hasActor = !!actor;
        this.showActor = this.hasActor;  // may change later... controls if we should actually SHOW the actor (in case you unexpectedly have it)
        // TASK THINGS
        this.task = task ?? {};
        this.hasTask = !!task;
        // do not use '#' as default for URL -- do that in template if needed -- because we want to be able to check for falsey value in URL
        this.taskUrl = this.isTuInternal ? (this.task?.urls?.internal_detail_in_workflow ?? '') : (this.task?.urls?.external_detail_in_workflow ?? '');
        this.taskTitle = this.task?.title || this.data?.task?.title || '';  // try to get it from task object, but maybe not there if we deleted the task, then try to get from whatever we maybe stashed in data about the task if its there
        //this.workflow = workflow_id ?? '';
        this.workflowId = workflow_id ?? '';
        // APPROVAL THINGS
        this.isApprovalEvent = !!this.data.approval_record;
        // does not always work, like if we deleted tasks - this.isTaskEvent = this.hasTask && !this.isApprovalEvent;  // a NON-APPROVAL TASK-SPECIFIC event
        this.isTaskEvent = is_task_event;
        // IF ITS A TASK EVENT, BUT WE DO NOT HAVE TASK TITLE, WE WILL HIDE ENTIRE RECORD (should only happen for old things where we didn't stash task data in 'data')
        if (this.isTaskEvent && !this.taskTitle) {
            this.isVisible = false;
        }
        this.isApprovalApproved = !!this.data.approval_record?.was_approved;
        this.isApprovalRejected = this.data.approval_record?.was_approved === false;  // check if it was explicitly false, not undefined
        this.wasApproved = this.data?.approval_record?.was_approved;  // will be true, false, or undefined
        this.approvalComment = this.data?.approval_record?.comment;
        // MESSAGE THINGS - handle approval messages separately?
        this.message = this.data?.message;  // in backend, if you want to show something in message, make sure its top-level in EventRecord.data
        // STYLE THINGS
        this.markerClasses = [];  // add them as you go
        if (this.isApprovalEvent) {
            this.markerClasses.push('is-approval-event');
        }
        if (this.isApprovalApproved) {
            this.markerClasses.push('is-approval-approved');
        }
        if (this.isApprovalRejected) {
            this.markerClasses.push('is-approval-rejected');
        }
        // WORKFLOW RESET
        this.isWorkflowReset = this.data?.trigger_name === 'go_reject_completed';
        if (this.isWorkflowReset) {
            this.markerClasses.push('is-workflow-reset');
        }
        // TOP LEVEL WORKFLOW THINGS
        this.workflowVerb = this.hasTask ? '' : this.verb;  // only for top-level workflow things, keep empty if its a task-level thing
        this.isWorkflowEvent = !this.isTaskEvent;
        this.isWorkflowCreated = this.workflowVerb === 'created';
        this.isWorkflowStarted = this.workflowVerb === 'started';
        this.isWorkflowCompleted = this.workflowVerb === 'completed';
        this.workflowEventText = '';  // just a default... may change below
        if (this.isWorkflowEvent) {
            this.markerClasses.push('is-workflow-event');
            this.markerClasses.push(`is-workflow-${this.workflowVerb}`);            
            // now set 'workflowEventText' -- a full string to display if this is not a task-level event
            switch (this.workflowVerb) {
                case 'created':  // should have actor here...
                    this.workflowEventText = `created the workflow`;  // use with actor in template
                    break;
                case 'started':  // should have actor here...
                    this.workflowEventText = `started the workflow`;  // use with actor in template
                    break;
                case 'completed':  // probably don't have actor here...
                    this.showActor = false;  // force it to not show
                    this.workflowEventText = `The workflow was completed`;
                    break;
                default:
                    this.workflowEventText = '';
                    break;
            }
        }
    }
}

export default {
    name: 'WorkflowRunHistory',
    components: {
        Timeline,
    },
    mixins: [
        errorHandling,
        currentUserInfoMixin,
    ],
    props: {
        runId: {
            type: String,
            required: true,
        }
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            loading: false,
            records: [],  // set ARRAY of records here from API data
        }
    },
    computed: {
        visibleRecords() {
            // return this.records, but filter our any where isVisible = false
            return this.records.filter(rec => rec.isVisible);
        }
    },
    mounted() {
        this.getRecords();
    },
    methods: {
        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage
        async getRecords() {
            this.loading = true;
            try {
                const { data } = await suppliosApi.workflowRun.get_event_records(this.runId);
                this.setLocalData(data);
            } catch (err) {
                this.apiErrorHandler(err);
            }
            this.loading = false;
        },
        setLocalData(apiRecords) {
            // set local data from api response data - will be an array of records
            const isTuInternal = this.$currentUserInfo.is_internal;  // add this so we know what URLs to show
            this.records = apiRecords.map(rec => {
                const recordData = {...rec, isTuInternal };
                return new TimelineRecord(recordData);
            });
        }
    }
}
</script>

<style lang="scss">
// not using scoped so it is easier to control deep components
// instead, just scoping in a custom class for the component
.workflow-run-history-timeline {

    .p-timeline-event-marker {
        border-color: var(--primary-600);
        background-color: var(--primary-100);
        &.is-approval-rejected {
            border-color: var(--danger-600);
            background-color: var(--danger-100);
        }
        &.is-approval-approved {
            border-color: var(--success-600);
            background-color: var(--success-100);
        }
        &.is-workflow-reset {
            border-color: var(--secondary-600);
            background-color: var(--secondary-100);
        }
        &.is-workflow-event {  // filled dark circles for major workflow events
            border-color: var(--gray-900);
            background-color: var(--gray-400);
        }
    }

    .p-timeline-event-opposite {
        width: 25%;  // not sure if this does anything... must be fighting other flex things
        max-width: 300px;
    }
    .p-timeline-event-content {
        width: 75%;
        max-width: 600px;
    }
}


</style>